// What this browser remembers about the guidance, and nothing else (ADR-0016).
//
// Only two preferences about a panel live here — whether the tour has been
// opened, and whether the strip is collapsed for now. The milestones themselves
// are server truth and are never cached, so nothing in this file can make the
// product claim something the database disagrees with.
//
// Blocked storage is a normal condition (private windows, a locked-down
// browser), not an error: every read falls back to "not seen", every write is
// allowed to do nothing.

const PREFIX = "simino.engagement";

const key = (name: string, workspaceId: string) => `${PREFIX}.${name}.${workspaceId}`;

function read(name: string, workspaceId: string): string | null {
  try {
    return localStorage.getItem(key(name, workspaceId));
  } catch {
    return null;
  }
}

function write(name: string, workspaceId: string, value: string): void {
  try {
    localStorage.setItem(key(name, workspaceId), value);
  } catch {
    // Not remembering is a small loss; failing here would not be.
  }
}

/** Has the tour ever been opened for this workspace, on this browser? */
export const hasSeenTour = (workspaceId: string): boolean =>
  read("tour", workspaceId) === "seen";

export const markTourSeen = (workspaceId: string): void =>
  write("tour", workspaceId, "seen");

/**
 * The strip is collapsed for this tab only — sessionStorage, not local.
 *
 * Collapsing is "not now", not "never again": someone who hides it while
 * finishing one job should still be met by it tomorrow, because the steps it is
 * counting are steps they have genuinely not done yet.
 */
export function isStripCollapsed(workspaceId: string): boolean {
  try {
    return sessionStorage.getItem(key("strip", workspaceId)) === "collapsed";
  } catch {
    return false;
  }
}

export function setStripCollapsed(workspaceId: string, collapsed: boolean): void {
  try {
    const k = key("strip", workspaceId);
    if (collapsed) sessionStorage.setItem(k, "collapsed");
    else sessionStorage.removeItem(k);
  } catch {
    // As above.
  }
}
